import styled from "@emotion/styled";
import { useNotificationContext } from "@hooks/context/useNotificationContext";
import {
  findSupportAction,
  SUPPORT_ACTION_ID,
  useSupportActions
} from "@hooks/utils/support/useSupportActions";
import { IconButton } from "@mui/material";
import BaseTooltip from "@ui/atoms/BaseTooltip/BaseTooltip";
import Icon from "@ui/atoms/Icon/Icon";
import { TOP_BAR_GROUP_GAP } from "@ui/organisms/Header/Header.components";

const BADGE_SIZE = 8;

const UtilityRow = styled("div")({
  alignItems: "center",
  display: "flex",
  flex: "none",
  gap: TOP_BAR_GROUP_GAP
});

const UtilityButton = styled(IconButton)(({ theme }) => ({
  "&& svg": {
    height: theme.controls.iconSize,
    width: theme.controls.iconSize
  },
  "&:hover": {
    backgroundColor: theme.vars.ui.sidebar.item.selected.background
  },
  backgroundColor: theme.vars.ui.sidebar.item.selected.background,
  border: `1px solid ${theme.vars.ui.sidebar.item.selected.ring}`,
  borderRadius: theme.shape.borderRadiusButton,
  boxSizing: "border-box",
  color: theme.vars.ui.sidebar.item.selected.text,
  height: theme.controls.height,
  position: "relative",
  width: theme.controls.height
}));

const UnreadBadge = styled("span")(({ theme }) => ({
  backgroundColor: theme.vars.ui.sidebar.item.selected.text,
  borderRadius: 99,
  height: BADGE_SIZE,
  position: "absolute",
  right: 3,
  top: 3,
  width: BADGE_SIZE
}));

/**
 * Support and Notifications: the last two controls in the bar, on every page.
 */
export const AppTopBarUtilityActions = () => {
  const supportActions = useSupportActions();
  const { openNotificationDrawer, unreadCount } = useNotificationContext();

  const contactSupport = findSupportAction(
    supportActions,
    SUPPORT_ACTION_ID.contactSupport
  );

  return (
    <UtilityRow data-test-id="app-top-bar-utility-actions">
      {contactSupport ? (
        <BaseTooltip title="Contact Support" placement="bottom">
          <UtilityButton
            aria-label="Contact Support"
            onClick={contactSupport.onClick}
            data-test-id="top-bar-support-button"
          >
            <Icon name="support" />
          </UtilityButton>
        </BaseTooltip>
      ) : null}
      <BaseTooltip title="Notifications" placement="bottom">
        <UtilityButton
          // The dot is decorative, so the count has to reach a screen reader
          // through the label or it does not reach one at all.
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount} unread`
              : "Notifications"
          }
          onClick={openNotificationDrawer}
          data-test-id="top-bar-notifications-button"
        >
          <Icon name="notification" />
          {unreadCount > 0 ? (
            <UnreadBadge data-test-id="top-bar-notifications-badge" />
          ) : null}
        </UtilityButton>
      </BaseTooltip>
    </UtilityRow>
  );
};
